import axios from 'axios';
import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';

import history from '~/services/history';
import notification from '~/services/notification';

import justificationsService from './justifications-service';
import * as justificationsActions from './justifications-actions';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM,
  SEARCH
} from './justifications-constants';

import { ISA_ERROR } from '~/constants';
import { formatMessage } from '~/i18n/translate';

import { AppStateType } from '../root-reducer';

let cancel: any;
const CancelToken = axios.CancelToken;

const INITIAL_VALUES = {
  description: ''
};

export function* getAll() {
  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(justificationsActions.setLoadingData(true));

    const { rowsPerPage, page, order, orderBy } = yield select((state: AppStateType) => state.justifications.list);

    const pagination = {
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const [{ data }, { data: count }] = yield all([
      call(
        justificationsService.getAll,
        {
          ...pagination
        },
        cancelToken
      ),
      call(justificationsService.getAllCount, cancelToken)
    ]);

    yield put(
      justificationsActions.getAllSuccess({
        rows: data,
        count
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(justificationsActions.setLoadingData(false));
  }
}

export function* getById({ id }: any) {
  try {
    yield put(justificationsActions.setLoadingData(true));

    if (!id) {
      yield put(
        justificationsActions.getByIdSuccess({
          data: INITIAL_VALUES
        })
      );
    } else {
      const { data } = yield call(justificationsService.getJustification, id);

      if (data) {
        yield put(
          justificationsActions.getByIdSuccess({
            data: data[0]
          })
        );
      }
    }
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(justificationsActions.setLoadingData(false));
  }
}

export function* save(data: any) {
  try {
    const { id, payload } = data;
    yield put(justificationsActions.setLoadingSend(true));

    if (id) {
      const { isConfirmed } = yield call(
        notification.confirm,
        formatMessage('commonLabelEdit'),
        formatMessage('justificationFormModalUpdateMessage')
      );

      if (isConfirmed) {
        yield call(justificationsService.editJustification, { id: payload?._id, payload });

        yield call(history.push, `/register/justifications/`, {
          date: new Date().getTime()
        });
        yield put(justificationsActions.setLoadingSend(false));
        yield call(
          notification.success,
          id ? formatMessage('justificationsFormUpdatedMessage') : formatMessage('justificationsFormCreatedMessage')
        );
      } else {
        yield put(justificationsActions.setLoadingSend(false));
      }
    } else {
      const parsedData = {
        ...payload,
        internalCode: new Date().getTime()
      };

      yield call(justificationsService.createJustification, parsedData);

      yield call(history.push, `/register/justifications/`, {
        date: new Date().getTime()
      });
      yield put(justificationsActions.setLoadingSend(false));
      yield call(
        notification.success,
        id ? formatMessage('justificationsFormUpdatedMessage') : formatMessage('justificationsFormCreatedMessage')
      );
    }
  } catch (error) {
    yield put(justificationsActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('justificationFormModalDeleteMessage')
    );
    if (isConfirmed) {
      yield put(justificationsActions.setLoadingSend(true));
      yield call(justificationsService.deleteJustification, id);
      yield put(justificationsActions.setLoadingSend(false));

      yield put(justificationsActions.getAllRequest());
      yield call(notification.success, formatMessage('justificationsFormDeleteMessage'));
    }
  } catch (error) {
    yield put(justificationsActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/register/justifications/edit/${id}`);
}

export function* searchTable({ queryString }: any) {
  yield delay(1000);

  if (cancel !== undefined) cancel();
  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  try {
    yield put(justificationsActions.setLoadingData(true));

    const { rowsPerPage, page, order, orderBy } = yield select((state: AppStateType) => state.justifications.list);

    const pagination = {
      skip: page === 0 ? 0 : page * rowsPerPage,
      limit: rowsPerPage,
      order,
      orderBy
    };

    const [{ data }, { data: count }] = yield all([
      call(
        justificationsService.getAll,
        {
          ...pagination,
          queryString
        },
        cancelToken
      ),
      call(justificationsService.getAllCount, cancelToken)
    ]);

    yield put(
      justificationsActions.getAllSuccess({
        rows: data,
        count
      })
    );
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(justificationsActions.setLoadingData(false));
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(SEARCH, searchTable)
]);
